import { useState } from 'preact/hooks';
import { api, ApiError, type LoginResponse } from '../services/api';
import { authState } from '../App';
import { LoadingSpinner } from '../components/LoadingSpinner';

export function LoginPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    churchSubdomain: '',
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [availableChurches, setAvailableChurches] = useState<Array<{ name: string; subdomain: string }>>([]);
  const [showChurchSelection, setShowChurchSelection] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleLogin = async (e: Event) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const { email, password, churchSubdomain } = formData;
      
      if (!email || !password) {
        setError('Please enter both email and password');
        setIsLoading(false);
        return;
      }

      const response: LoginResponse = await api.login(email, password, churchSubdomain || undefined);
      
      if (response.requiresChurchSelection && response.availableChurches) {
        setAvailableChurches(response.availableChurches);
        setShowChurchSelection(true);
        setIsLoading(false);
        return;
      }
      
      if (response.success) {
        authState.value = {
          isAuthenticated: true,
          isLoading: false,
          user: response.user,
          church: response.church,
        };
      } else {
        setError('Login failed. Please try again.');
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setError('Invalid email or password');
        } else if (err.code === 'NETWORK_ERROR') {
          setError('Network error. Please check your connection and try again.');
        } else {
          setError(err.message);
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChurchSelect = (subdomain: string) => {
    setFormData(prev => ({ ...prev, churchSubdomain: subdomain }));
    setShowChurchSelection(false);
    setTimeout(() => {
      const form = document.getElementById('login-form') as HTMLFormElement;
      form?.requestSubmit();
    }, 100);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--parchment)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 16px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Subtle field-note background texture via radial dots */}
      <div aria-hidden="true" style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(circle, rgba(25,38,49,0.05) 1px, transparent 1px)',
        backgroundSize: '28px 28px',
        opacity: 0.6,
      }} />

      {/* Warm gradient wash */}
      <div aria-hidden="true" style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse 70% 60% at 70% 80%, rgba(244,182,64,0.08) 0%, transparent 70%)',
      }} />

      <div style={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }} className="animate-slide-up">

        {/* Brand header */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{
            width: 56, height: 56,
            background: 'var(--marigold)',
            borderRadius: '13px 13px 13px 3px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
            boxShadow: '0 8px 24px rgba(244, 182, 64, 0.32)',
          }}>
            <svg width="28" height="28" fill="none" stroke="var(--ink)" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>

          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 700,
            letterSpacing: '0.16em', textTransform: 'uppercase',
            color: 'var(--ink-faint)', margin: '0 0 6px',
          }}>
            Church Admin Portal
          </p>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 600,
            letterSpacing: '-0.05em', color: 'var(--ink)',
            margin: '0 0 8px',
          }}>
            Welcome to <em style={{ fontStyle: 'italic' }}>WeShephard</em>
          </h1>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 14,
            color: 'var(--ink-soft)', margin: 0,
          }}>
            Sign in to manage check-ins and care records.
          </p>
        </div>

        {/* Church Selection */}
        {showChurchSelection && (
          <div style={{
            background: 'var(--paper)',
            border: '1px solid var(--border)',
            borderRadius: '18px 18px 48px 18px',
            padding: 28,
            boxShadow: 'var(--shadow-card)',
            marginBottom: 16,
          }}>
            {/* Tape accent */}
            <div style={{
              position: 'relative', marginBottom: 20, paddingTop: 8,
            }}>
              <div style={{
                position: 'absolute', left: 0, top: 0,
                width: 44, height: 8, background: 'var(--marigold)',
                borderRadius: 2, transform: 'rotate(-2deg)',
              }} />
            </div>
            <p className="mini-label" style={{ margin: '0 0 4px' }}>Multiple churches found</p>
            <h3 style={{
              fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 600,
              letterSpacing: '-0.04em', color: 'var(--ink)', margin: '0 0 8px',
            }}>
              Select Your Church
            </h3>
            <p style={{ fontSize: 13, color: 'var(--ink-soft)', margin: '0 0 20px' }}>
              You have access to multiple churches. Please select which one you'd like to access.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {availableChurches.map((church, i) => (
                <button
                  key={church.subdomain}
                  onClick={() => handleChurchSelect(church.subdomain)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    width: '100%', textAlign: 'left',
                    padding: '14px 16px',
                    background: 'var(--parchment)',
                    border: '1.5px solid var(--border)',
                    borderRadius: 12, cursor: 'pointer',
                    fontFamily: 'var(--font-body)',
                    transition: 'all 160ms var(--ease-out)',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'var(--marigold)';
                    (e.currentTarget as HTMLElement).style.background = 'var(--card-tint-cream)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)';
                    (e.currentTarget as HTMLElement).style.background = 'var(--parchment)';
                  }}
                >
                  <div style={{
                    width: 36, height: 36, flexShrink: 0,
                    background: 'var(--marigold-soft)',
                    borderRadius: '11px 11px 11px 3px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '1.5px solid var(--marigold)',
                  }}>
                    <span style={{
                      fontFamily: 'var(--font-display)', fontWeight: 700,
                      fontSize: 12, color: '#7D5507',
                    }}>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)', margin: 0 }}>
                      {church.name}
                    </p>
                    <p style={{ fontSize: 11, color: 'var(--ink-faint)', margin: '2px 0 0', fontWeight: 600, letterSpacing: '0.06em' }}>
                      @{church.subdomain}
                    </p>
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowChurchSelection(false)}
              className="btn-secondary"
              style={{ width: '100%', marginTop: 16 }}
            >
              ← Back to Login
            </button>
          </div>
        )}

        {/* Login Form */}
        {!showChurchSelection && (
          <form
            id="login-form"
            onSubmit={handleLogin}
            style={{
              background: 'var(--paper)',
              border: '1px solid var(--border)',
              borderRadius: '18px 18px 110px 18px',
              padding: '32px',
              boxShadow: 'var(--shadow-card)',
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
              position: 'relative',
            }}
          >
            {/* Tape accent on the care-atlas card */}
            <div style={{
              position: 'absolute', left: 24, top: -8,
              width: 52, height: 9, background: 'var(--marigold)',
              borderRadius: 2, transform: 'rotate(-2deg)', opacity: 0.9,
            }} />

            {/* Error */}
            {error && (
              <div className="alert-error">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20" style={{ flexShrink: 0 }}>
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p style={{ fontSize: 13, margin: 0 }}>{error}</p>
              </div>
            )}

            {/* Email */}
            <div>
              <label htmlFor="email" className="label">Email Address</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onInput={(e) => handleInputChange('email', (e.target as HTMLInputElement).value)}
                className="input"
                placeholder="admin@yourchurch.com"
                disabled={isLoading}
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="label">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onInput={(e) => handleInputChange('password', (e.target as HTMLInputElement).value)}
                  className="input"
                  style={{ paddingRight: 44 }}
                  placeholder="Enter your password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute', right: 12, top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    color: 'var(--ink-faint)', padding: 4,
                    transition: 'color 160ms',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = 'var(--ink-soft)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'var(--ink-faint)'; }}
                >
                  {showPassword ? (
                    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Church subdomain */}
            <div>
              <label htmlFor="churchSubdomain" className="label">
                Church Subdomain
                <span className="label-optional">optional</span>
              </label>
              <input
                id="churchSubdomain"
                name="churchSubdomain"
                type="text"
                value={formData.churchSubdomain}
                onInput={(e) => handleInputChange('churchSubdomain', (e.target as HTMLInputElement).value)}
                className="input"
                placeholder="your-church-name"
                disabled={isLoading}
              />
              <p className="field-hint">Only required if you have access to multiple churches</p>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary"
              style={{ width: '100%', padding: '14px 24px', fontSize: 14, marginTop: 4 }}
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="small" />
                  Signing in…
                </>
              ) : (
                <>
                  Sign In
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </>
              )}
            </button>
          </form>
        )}

        {/* Register link */}
        <div style={{ textAlign: 'center', marginTop: 28 }}>
          <p style={{ fontSize: 13, color: 'var(--ink-soft)', margin: '0 0 12px' }}>
            Don't have a church account yet?
          </p>
          <a
            href="/register"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '10px 20px',
              border: '1.5px solid var(--border)',
              borderRadius: 999,
              fontSize: 12, fontWeight: 700,
              letterSpacing: '0.06em',
              color: 'var(--ink-soft)',
              textDecoration: 'none',
              fontFamily: 'var(--font-body)',
              background: 'var(--paper)',
              transition: 'all 160ms var(--ease-out)',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.borderColor = 'var(--marigold)';
              (e.currentTarget as HTMLElement).style.color = 'var(--ink)';
              (e.currentTarget as HTMLElement).style.background = 'var(--card-tint-cream)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)';
              (e.currentTarget as HTMLElement).style.color = 'var(--ink-soft)';
              (e.currentTarget as HTMLElement).style.background = 'var(--paper)';
            }}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Register Your Church
          </a>
        </div>

        {/* Footer */}
        <p style={{
          textAlign: 'center', marginTop: 24,
          fontSize: 10, fontWeight: 700, letterSpacing: '0.12em',
          textTransform: 'uppercase', color: 'var(--ink-faint)',
        }}>
          WeShephard · Care Atlas Platform
        </p>
      </div>
    </div>
  );
}